import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../auth/AuthContext';
import API_BASE from '../../utils/api';

function formatCurrency(cents) {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// The subscription switches to Standard right away; unused Premium time for
// the rest of the billing period is credited on the next invoice.
export default function DowngradeToStandardModal({ onClose }) {
  const { auth, setPlan } = useContext(AuthContext);

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchPreview() {
      try {
        const res = await fetch(`${API_BASE}/api/stripe/preview-downgrade`, {
          headers: { Authorization: `Bearer ${auth.idToken}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load pricing');
        setPreview(data);
      } catch (err) {
        setPreviewError(err.message);
      }
    }
    fetchPreview();
  }, [auth.idToken]);

  async function handleDowngrade(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/stripe/downgrade-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.idToken}`,
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to switch plans');

      setPlan('standard');
      onClose();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  const nextInvoice = preview?.nextInvoiceDate ? ` on ${formatDate(preview.nextInvoiceDate)}` : '';
  const amount = preview?.prorationAmount;

  return (
    <div className="upgrade-modal-overlay" onClick={loading ? undefined : onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Switch to Standard</h2>

        <p className="upgrade-modal-desc">
          You'll lose access to Premium-only features — Time Billing and QuickBooks integration — as
          soon as you switch.
        </p>

        <div className="upgrade-price-row">
          {previewError ? (
            <span className="upgrade-price-error">{previewError}</span>
          ) : !preview ? (
            <span className="upgrade-price-loading">Calculating price…</span>
          ) : amount == null ? (
            <span className="upgrade-price-label">
              No charge — any unused Premium time will be credited on your next invoice{nextInvoice}.
            </span>
          ) : amount < 0 ? (
            <>
              <span className="upgrade-price-amount">{formatCurrency(-amount)}</span>
              <span className="upgrade-price-label">
                credited on your next invoice{nextInvoice} for unused Premium time
              </span>
            </>
          ) : amount > 0 ? (
            <>
              <span className="upgrade-price-amount">{formatCurrency(amount)}</span>
              <span className="upgrade-price-label">added to your next invoice{nextInvoice}</span>
            </>
          ) : (
            <span className="upgrade-price-label">
              No charge and no credit — your plan switches right away.
            </span>
          )}
        </div>

        <form onSubmit={handleDowngrade}>
          {error && <p className="upgrade-modal-error">{error}</p>}

          <div className="upgrade-modal-actions">
            <button type="button" className="upgrade-cancel-btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              className="upgrade-confirm-btn"
              disabled={loading || !preview || !!previewError}
            >
              {loading ? 'Switching…' : 'Switch to Standard'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
